<template>
  <div class="relative flex min-h-0 flex-col overflow-hidden">
    <!-- Zwei Spalten: Light links, Dark rechts -->
    <div class="flex min-h-0 flex-1 flex-col">
      <div class="grid grid-cols-2 gap-8">
        <!-- Light Themes -->
        <div v-if="lightThemes.length > 0">
          <h3 class="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
            Lightmodes
          </h3>
        </div>

        <!-- Dark Themes -->
        <div v-if="darkThemes.length > 0">
          <h3 class="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
            Darkmodes
          </h3>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div class="grid grid-cols-2 gap-8">
          <!-- Light Themes -->
          <div
            v-if="lightThemes.length > 0"
            class="space-y-1.5"
          >
            <button
              v-for="theme in lightThemes"
              :key="theme.id"
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-all hover:shadow-md',
                isSelectedLight(theme)
                  ? 'border-accent ring-1 ring-accent/50 bg-accent/10'
                  : highlightedThemeId === theme.id
                    ? 'border-accent ring-1 ring-accent/40 bg-accent/10'
                    : 'border-secondary/30 hover:border-secondary/70',
              ]"
              :style="{
                '--highlight-accent':
                  highlightedThemeId === theme.id ? theme.tokens.accent : undefined,
                animation: highlightedThemeId === theme.id ? 'pulse-ring 0.6s ease-out' : undefined,
              }"
              @click="selectLightTheme(theme)"
            >
              <span class="flex h-7 w-14 shrink-0 gap-0.5 overflow-hidden rounded">
                <span
                  v-for="(tokenName, idx) in TOKEN_ORDER"
                  :key="idx"
                  class="h-full flex-1"
                  :style="{ backgroundColor: theme.tokens[tokenName as keyof ThemeTokens] }"
                />
              </span>

              <span class="min-w-0 flex-1">
                <span class="truncate text-sm font-medium text-text-main">{{ theme.label }}</span>
                <span class="block truncate font-mono text-[10px] opacity-40">{{ theme.id }}</span>
              </span>

              <svg
                v-if="isSelectedLight(theme)"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                class="text-accent shrink-0"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          </div>

          <!-- Dark Themes -->
          <div
            v-if="darkThemes.length > 0"
            class="space-y-1.5"
          >
            <button
              v-for="theme in darkThemes"
              :key="theme.id"
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-all hover:shadow-md',
                isSelectedDark(theme)
                  ? 'border-accent ring-1 ring-accent/50 bg-accent/10'
                  : highlightedThemeId === theme.id
                    ? 'border-accent ring-1 ring-accent/40 bg-accent/10'
                    : 'border-secondary/30 hover:border-secondary/70',
              ]"
              :style="{
                '--highlight-accent':
                  highlightedThemeId === theme.id ? theme.tokens.accent : undefined,
                animation: highlightedThemeId === theme.id ? 'pulse-ring 0.6s ease-out' : undefined,
              }"
              @click="selectDarkTheme(theme)"
            >
              <span class="flex h-7 w-14 shrink-0 gap-0.5 overflow-hidden rounded">
                <span
                  v-for="(tokenName, idx) in TOKEN_ORDER"
                  :key="idx"
                  class="h-full flex-1"
                  :style="{ backgroundColor: theme.tokens[tokenName as keyof ThemeTokens] }"
                />
              </span>

              <span class="min-w-0 flex-1">
                <span class="truncate text-sm font-medium text-text-main">{{ theme.label }}</span>
                <span class="block truncate font-mono text-[10px] opacity-40">{{ theme.id }}</span>
              </span>

              <svg
                v-if="isSelectedDark(theme)"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                class="text-accent shrink-0"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Theme, ThemeTokens } from '@/composables/useThemeNew'
import { setActiveThemeMode } from '@/composables/useThemeNew'

/** ID des gerade ausgewählten Themes — für die Highlight-Animation */
const highlightedThemeId = ref<string | null>(null)
let highlightTimeout: ReturnType<typeof setTimeout> | null = null

function clearHighlight(): void {
  if (highlightTimeout) clearTimeout(highlightTimeout)
  highlightedThemeId.value = null
}

/** Startet das Highlight für ein Theme und löscht es nach 1.5s */
function startHighlight(themeId: string): void {
  highlightedThemeId.value = themeId
  clearHighlight()
  highlightTimeout = setTimeout(clearHighlight, 1500)
}

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select', themeId: string): void
}>()

interface Props {
  themes: Theme[]
}

const props = defineProps<Props>()

const TOKEN_ORDER: Array<keyof ThemeTokens> = ['primary', 'secondary', 'tertiary', 'accent', 'text']

// Nach Mode gespalten —直接从 Parent Props (NOT useTheme! every instance has its own)
const lightThemes = computed(() => props.themes.filter((t) => t.mode === 'light'))
const darkThemes = computed(() => props.themes.filter((t) => t.mode === 'dark'))

/** Reaktiv: aktuell angewählte Light-Theme-ID (immer aus localStorage, nie useTheme) */
function getActiveLightId(): string {
  const fromStorage = localStorage.getItem('selected-light-theme-id')
  return fromStorage || ''
}

/** Reaktiv: aktuell angewählte Dark-Theme-ID (immer aus localStorage, nie useTheme) */
function getActiveDarkId(): string {
  const fromStorage = localStorage.getItem('selected-dark-theme-id')
  return fromStorage || ''
}

function isSelectedLight(theme: Theme): boolean {
  return theme.mode === 'light' && getActiveLightId() === theme.id
}

function isSelectedDark(theme: Theme): boolean {
  return theme.mode === 'dark' && getActiveDarkId() === theme.id
}

/** Wählt ein Light-Theme — speichert es, meldet App.vue per Window-Event und emittiert an Parent */
function selectLightTheme(theme: Theme): void {
  localStorage.setItem('selected-light-theme-id', theme.id)
  startHighlight(theme.id)
  setActiveThemeMode('light') // CSS-Variablen + currentThemesRef aktualisieren
  window.dispatchEvent(new CustomEvent('themeSelect', { detail: { id: theme.id } })) // App.vue syncs Pill + alles
  emit('select', theme.id)
}

/** Wählt ein Dark-Theme — speichert es, meldet App.vue per Window-Event und emittiert an Parent */
function selectDarkTheme(theme: Theme): void {
  localStorage.setItem('selected-dark-theme-id', theme.id)
  startHighlight(theme.id)
  setActiveThemeMode('dark') // CSS-Variablen + currentThemesRef aktualisieren
  window.dispatchEvent(new CustomEvent('themeSelect', { detail: { id: theme.id } })) // App.vue syncs Pill + alles
  emit('select', theme.id)
}
</script>

<style scoped>
@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 var(--highlight-accent, rgba(228, 0, 58, 0.4));
  }
  40% {
    box-shadow: 0 0 0 6px var(--highlight-accent, rgba(228, 0, 58, 0));
  }
  100% {
    box-shadow: 0 0 0 0 var(--highlight-accent, rgba(228, 0, 58, 0));
  }
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: var(--color-tertiary);
  border-radius: 3px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: var(--color-text);
}
</style>
